import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import { XMarkIcon } from '@heroicons/react/24/outline';
import _isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationStrategyDrawer
	from '@app/site/secured/rateshopper/pricing-automation/architecture/overlay/PricingAutomationStrategyDrawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import { PencilSquareIcon } from '@heroicons/react/24/solid';
import PricingArchitectureDerivative from '@app/model/pricing-automation/PricingArchitectureDerivative';
import EditPricingArchitectureDerivativeRuleModal
	from '@app/site/secured/rateshopper/pricing-automation/architecture/overlay/EditPricingArchitectureDerivativeRuleModal';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import CompanyHotelPriceRecommendationEngineVersion
	from '@app/model/company_hotel/CompanyHotelPriceRecommendationEngineVersion';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import PricingRoomName from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName';
import { pricingRoomName } from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName.utilities';

const PricingArchitectureFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	architecture: PricingArchitecture,
	rank?: number,
	rankOptions?: number,
	isRankUpdating?: boolean,
	onRankChange?: (architectureId: number, rank: number) => any,
}> = ({ companyHotel, architecture, rank, rankOptions = 0, isRankUpdating = false, onRankChange }) => {


	const removeArchitectureDialog = useDialogue<number>();
	const removeArchitectureDerivativeDialog = useDialogue<{ architectureId: number, derivativeId: number }>();
	const [editSelectedDerivative, setSelectedDerivative] = useState<PricingArchitectureDerivative | undefined>(undefined);
	const drawer = useDrawer();

	const toaster = useToaster();

	const queryClient = useQueryClient();

	const removeArchitectureMutation = useMutation({
		mutationFn: PricingAutomationService.removePricingArchitecture,
		onSuccess: (architectures) => {
			queryClient.setQueryData(PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id), architectures);
			queryClient.invalidateQueries({
				queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotel.id),
			}).then(() => toaster.success('Automation', 'Architecture removed successfully'));
		},
		onError: (error) => {
			toaster.error('Automation', 'Failed to remove architecture. Please check if there is a strategy using this architecture.');
		},
	});
	const removeArchitectureDerivativeMutation = useMutation({
		mutationFn: PricingAutomationService.removePricingArchitectureDerivative,
		onSuccess: (architectures) => {
			queryClient.setQueryData(PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id), architectures);
			queryClient.invalidateQueries({
				queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotel.id),
			}).then(() => toaster.success('Automation', 'Architecture derivative removed successfully'));
		},
		onError: (error) => {
			toaster.error('Automation', 'Failed to remove architecture derivative');
		},
	});

	const removeArchitectureDialogHandler = (architectureId: number) => {
		removeArchitectureDialog.open(architectureId);
	};

	const removeArchitecture = (architectureId: number) => {
		removeArchitectureMutation.mutate({ companyHotelId: companyHotel.id, architectureId });
	};

	const removeArchitectureDerivativeDialogHandler = (architectureId: number, derivativeId: number) => {
		removeArchitectureDerivativeDialog.open({ architectureId, derivativeId });
	};

	const removeArchitectureDerivative = (input: { architectureId: number, derivativeId: number }) => {
		const { architectureId, derivativeId } = input;
		removeArchitectureDerivativeMutation.mutate({
			companyHotelId: companyHotel.id,
			architectureId,
			derivativeId,
		});
	};

	const hasPriceRecommendationModule = companyHotel.hasPriceRecommendationModule();

	return <>
		<Dialogue ref={removeArchitectureDialog.ref} title="Remove Architecture"
		          onConfirm={() => removeArchitecture(removeArchitectureDialog.getValue()!)}>
			Are you sure you want to remove this architecture?
		</Dialogue>
		<Dialogue ref={removeArchitectureDerivativeDialog.ref} title="Remove Architecture Derivative"
		          onConfirm={() => removeArchitectureDerivative(removeArchitectureDerivativeDialog.getValue()!)}>
			Are you sure you want to remove this architecture derivative?
		</Dialogue>
		<PricingAutomationStrategyDrawer architecture={architecture} strategy={architecture.strategy}
		                                 ref={drawer.ref} />
		<div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
			<div className="min-w-0">
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<div className="text-sm font-semibold text-gray-900"><PricingRoomName room={architecture.anchor} /></div>
					{hasPriceRecommendationModule && companyHotel.priceRecommendationSettings?.engineVersion === CompanyHotelPriceRecommendationEngineVersion.NORMAL && <>
						{architecture.hasStrategy &&
							<a className="text-xs text-blue-600 hover:underline cursor-pointer font-semibold mx-2"
							   onClick={() => drawer.open()}>Edit Strategy</a>}
						{!architecture.hasStrategy &&
							<a className="text-xs text-blue-600 hover:underline cursor-pointer font-semibold mx-2 flex items-baseline"
							   onClick={() => drawer.open()}>
							<span className="relative top-0.5 self-baseline mr-1">
								<ExclamationTriangleIcon className="h-4 w-4 text-yellow-400" />
							</span>
								<span>Create Strategy</span>
							</a>}
					</>}
				</div>
				<div className="mt-0.5 text-xs text-gray-500">
					Following market group: {Utilities.snakeCaseToWords(architecture.competitorsMappingType)}</div>
			</div>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
				{hasPriceRecommendationModule && !!rank && !!onRankChange && companyHotel.priceRecommendationSettings?.enforcePricingArchitectureHierarchy && <label className="flex items-center gap-x-1 text-xs font-semibold text-gray-900">
					<span>Rank</span>
					<select
						aria-label={`Change rank for ${pricingRoomName(architecture.anchor)}`}
						value={rank}
						disabled={isRankUpdating}
						onChange={event => onRankChange(architecture.id, Number(event.target.value) - 1)}
						className="h-9 rounded-md border-gray-300 py-1 pl-2 pr-8 text-sm font-semibold text-gray-900 shadow-sm focus:border-blue-600 focus:ring-blue-600 disabled:cursor-wait disabled:bg-gray-100"
					>
						{Array.from({ length: rankOptions }, (_, index) => index + 1).map(option => (
							<option key={option} value={option}>{option}</option>
						))}
					</select>
				</label>}
				<div className="text-xs font-medium text-gray-600">{architecture.derivatives.length} anchored</div>
				<button
					type="button"
					className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
					onClick={() => removeArchitectureDialogHandler(architecture.id)}
					title="Delete"
				>
					<XMarkIcon className="h-4 w-4" />
				</button>
			</div>
		</div>
		{!_isEmpty(architecture.derivatives) && (
			<div className="px-3 pb-3">
				<ul role="list" className="space-y-2">
					{architecture.derivatives.map((child) => (
						<li key={child.pricingRoom.id} className="rounded-md border border-gray-200 bg-white px-3 py-2">
							<div className="flex items-start justify-between gap-x-3">
								<div className="flex items-center gap-x-2">
									<div className="text-sm font-medium text-gray-900"><PricingRoomName room={child.pricingRoom} /></div>
									<div className="text-xs text-gray-500 ml-0.5 italic">
										({child.rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE &&
										`${child.rule.value! > 0 ? '+' : ''}${child.rule.value}%`}
										{child.rule.type === PricingArchitectureDerivativeRuleType.AMOUNT &&
											` ${child.rule.value! > 0 ? '+' : ''}${child.rule.value}${Utilities.formatCurrency(companyHotel.settings.main.currency)}`}
										{child.rule.type === PricingArchitectureDerivativeRuleType.SAME && 'Same Price'})
									</div>
								</div>
								<div className="flex items-center gap-x-1">
									<button
										type="button"
										className="rounded-md bg-white text-gray-400 hover:text-blue-500 focus:outline-none"
										onClick={() => setSelectedDerivative(child)}
										title="Edit"
									>
										<PencilSquareIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										className="rounded-md bg-white text-gray-400 hover:text-red-500 focus:outline-none"
										onClick={() => removeArchitectureDerivativeDialogHandler(architecture.id, child.id)}
										title="Delete"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							</div>
						</li>
					))}
				</ul>
			</div>
		)}
		{!!editSelectedDerivative &&
			<EditPricingArchitectureDerivativeRuleModal companyHotelId={companyHotel.id} architectureId={architecture.id}
			                                            derivative={editSelectedDerivative}
			                                            onClose={() => setSelectedDerivative(undefined)} />}
	</>;
};

export default PricingArchitectureFragment;
